import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { catchError, filter, map, Observable, of, switchMap } from 'rxjs';

import { MeetingService } from '../services/meeting.service';

import {
  fetchAgendaDecisionStatusOptions,
  fetchAgendaDecisionStatusOptionsFailure,
  fetchAgendaDecisionStatusOptionsSuccess,
  fetchMeetingContent,
  fetchMeetingContentFailure,
  fetchMeetingContentSuccess,
  fetchMeetingHeader,
  fetchMeetingHeaderFailure,
  fetchMeetingHeaderSuccess,
  fetchMeetingOptionsMenu,
  fetchMeetingOptionsMenuFailure,
  fetchMeetingOptionsMenuSuccess,
  fetchMeetingPrimaryActions,
  fetchMeetingPrimaryActionsFailure,
  fetchMeetingPrimaryActionsSuccess,
  fetchMeetingSidebar,
  fetchMeetingSidebarFailure,
  fetchMeetingSidebarSuccess,
  fetchMoveMeetingItemDates,
  fetchMoveMeetingItemDatesFailure,
  fetchMoveMeetingItemDatesSuccess,
  fetchMoveMeetingItemOrgans,
  fetchMoveMeetingItemOrgansFailure,
  fetchMoveMeetingItemOrgansSuccess,
  removeMeetingAttachment,
  removeMeetingAttachmentFailure,
  removeMeetingAttachmentSuccess,
  updateMeetingAdditionalInfo,
  updateMeetingAdditionalInfoFailure,
  updateMeetingAdditionalInfoSuccess,
  updateMeetingHeader,
  updateMeetingHeaderFailure,
  updateMeetingHeaderSuccess
} from './meeting.actions';

import { PanelMenuService } from '~/ui/panel-menu/panel-menu.service';

@Injectable()
export class MeetingEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly meetingService: MeetingService,
    private panelMenuService: PanelMenuService
  ) {}

  fetchMeetingHeader$ = createEffect(
    (): Observable<Action> =>
      this.actions$.pipe(
        ofType(fetchMeetingHeader),
        switchMap((action) =>
          this.meetingService.fetchMeetingHeader(action.id).pipe(
            map((data) => fetchMeetingHeaderSuccess({ data })),
            catchError((error: any) => of(fetchMeetingHeaderFailure({ error })))
          )
        )
      )
  );

  updateMeetingHeader$ = createEffect(
    (): Observable<Action> =>
      this.actions$.pipe(
        ofType(updateMeetingHeader),
        switchMap((action) =>
          this.meetingService.updateMeetingHeader(action.id, action.data).pipe(
            map((data) => updateMeetingHeaderSuccess({ data })),
            catchError((error: any) => of(updateMeetingHeaderFailure({ error })))
          )
        )
      )
  );

  fetchMeetingContent$ = createEffect(
    (): Observable<Action> =>
      this.actions$.pipe(
        ofType(fetchMeetingContent),
        switchMap((action) =>
          this.meetingService.fetchMeetingContent(action.id, action.criteria).pipe(
            map((data) => {
              if (action.criteria.showSummary) {
                return this.panelMenuService.toggleAllSummary(data, true, action?.prevMeetingItems);
              }

              return data;
            }),
            map((data) => fetchMeetingContentSuccess({ data })),
            catchError((error: any) => of(fetchMeetingContentFailure({ error })))
          )
        )
      )
  );

  fetchMeetingSidebar$ = createEffect(
    (): Observable<Action> =>
      this.actions$.pipe(
        ofType(fetchMeetingSidebar),
        switchMap((action) =>
          this.meetingService.fetchMeetingSidebar(action.id).pipe(
            map((data) => fetchMeetingSidebarSuccess({ data })),
            catchError((error: any) => of(fetchMeetingSidebarFailure({ error })))
          )
        )
      )
  );

  fetchMeetingPrimaryActions$ = createEffect(
    (): Observable<Action> =>
      this.actions$.pipe(
        ofType(fetchMeetingPrimaryActions),
        switchMap((action) =>
          this.meetingService.fetchMeetingPrimaryActions(action.id).pipe(
            map((data) => fetchMeetingPrimaryActionsSuccess({ data })),
            catchError((error: any) => of(fetchMeetingPrimaryActionsFailure({ error })))
          )
        )
      )
  );

  fetchMeetingOptionsMenuActions$ = createEffect(
    (): Observable<Action> =>
      this.actions$.pipe(
        ofType(fetchMeetingOptionsMenu),
        switchMap((action) =>
          this.meetingService.fetchMeetingOptionsMenu(action.id).pipe(
            map((data) => fetchMeetingOptionsMenuSuccess({ data })),
            catchError((error: any) => of(fetchMeetingOptionsMenuFailure({ error })))
          )
        )
      )
  );

  updateMeetingAdditionalInfo$ = createEffect(
    (): Observable<Action> =>
      this.actions$.pipe(
        ofType(updateMeetingAdditionalInfo),
        filter((action) => !!action.meetingId),
        switchMap((action) =>
          this.meetingService.updateMeetingAdditionalInfo(action.meetingId, action.data).pipe(
            map((data) => updateMeetingAdditionalInfoSuccess({ data })),
            catchError((error: any) => of(updateMeetingAdditionalInfoFailure({ error })))
          )
        )
      )
  );

  removeMeetingAttachment$ = createEffect(
    (): Observable<Action> =>
      this.actions$.pipe(
        ofType(removeMeetingAttachment),
        switchMap((action) =>
          this.meetingService.removeMeetingAttachment(action.meetingId, action.attachmentId).pipe(
            map(() => removeMeetingAttachmentSuccess({ id: action.attachmentId })),
            catchError((error: any) => of(removeMeetingAttachmentFailure({ error })))
          )
        )
      )
  );

  fetchAgendaDecisionStatusOptions = createEffect(
    (): Observable<Action> =>
      this.actions$.pipe(
        ofType(fetchAgendaDecisionStatusOptions),
        switchMap(() =>
          this.meetingService.fetchAgendaDecisionOptions().pipe(
            map((data) => fetchAgendaDecisionStatusOptionsSuccess({ data })),
            catchError((error: any) => of(fetchAgendaDecisionStatusOptionsFailure({ error })))
          )
        )
      )
  );

  fetchMoveMeetingItemOrgans = createEffect(
    (): Observable<Action> =>
      this.actions$.pipe(
        ofType(fetchMoveMeetingItemOrgans),
        switchMap((action) =>
          this.meetingService.fetchMoveMeetingItemOrgans(action.meetingId).pipe(
            map((data) => fetchMoveMeetingItemOrgansSuccess({ data })),
            catchError((error: any) => of(fetchMoveMeetingItemOrgansFailure({ error })))
          )
        )
      )
  );

  fetchMoveMeetingItemDates = createEffect(
    (): Observable<Action> =>
      this.actions$.pipe(
        ofType(fetchMoveMeetingItemDates),
        switchMap((action) =>
          this.meetingService.fetchMoveMeetingItemDates(action.meetingId, action.organId).pipe(
            map((data) => fetchMoveMeetingItemDatesSuccess({ data })),
            catchError((error: any) => of(fetchMoveMeetingItemDatesFailure({ error })))
          )
        )
      )
  );
}
